import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { withSnackbar } from 'notistack';
import layoutNames from 'utils/enums/layout-names.enum';
import { setNumberRowsPerPageActionCreator } from 'plugins/store/reducers/settings.reducer';
import { setPageHeaderTextActionCreator, setTableStateActionCreator } from 'plugins/store/reducers/common.reducer';
import { setAlertActionCreator } from 'plugins/store/reducers/common.reducer';
import { searchOrganizations, deleteOrganizationById } from 'services/organization.service';

// core components
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import CardIcon from 'components/Card/CardIcon';
import ReactTable from 'components/ReactTable/ReactTable';
import SnackMessage from 'components/Snackbar/SnackMessage';

import Tooltip from '@material-ui/core/Tooltip';
import BusinessOutlinedIcon from '@material-ui/icons/BusinessOutlined';
import AddCircleOutlineOutlinedIcon from '@material-ui/icons/AddCircleOutlineOutlined';
import { withStyles } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import styles from 'assets/jss/material-dashboard-pro-react/views/organizationTableStyle';


const customStyles = {
	...styles,
	imageLink: {
		cursor: 'pointer',
	},
};
class Organizations extends Component {
	viewName = 'Organizations';

	columns = [
		{
			Header: 'Name',
			accessor: 'name',
		},
		{
			Header: 'Actions',
			accessor: 'actions',
			disableFilters: true,
			disableSortBy: true,
			className: 'column-align-right',
			Cell: ({ row }) => {
				return (
					<div className={this.props.classes.actionsWrapper}>
						<Tooltip
							arrow
							title={"Remove"} >
							<DeleteIcon
								className={this.props.classes.actionIcon}
								onClick={(e) => this.onDeleteClick(e, row?.original?.id)}
							/>
						</Tooltip>
					</div>)
			},
		},
	];

	state = {
		data: [],
		totalRecordsCount: 0,
		pageCount: 0,
		loading: true,
	};

	componentDidMount() {
		this.props.setPageHeaderText(this.viewName);
	}

	componentWillUnmount() {
		this.props.setPageHeaderText('');
	}

	fetchData = async ({
		filterQuery,
		pageIndex,
		pageSize,
		sortBy,
	}) => {
		this.setState({ loading: true });
		const result = await searchOrganizations({
			filterQuery,
			pageIndex,
			pageSize,
			sortBy,
		});

		this.setState({
			data: result.data,
			totalRecordsCount: result.totalCount,
			pageCount: Math.ceil(result.totalCount / pageSize),
			loading: false,
		});
		this.props.setRowsPerPage({
			table: 'organizations',
			rowsPerPage: pageSize,
		});
		this.props.setTableState({
			table: 'organizations',
			state: {
				searchQuery: filterQuery,
				sortBy: sortBy,
				page: pageIndex,
			},
		});
	};

	onDeleteClick = (event, organizationId) => {
		event.stopPropagation();
		const {
			enqueueSnackbar,
			setAlert,
			rowsPerPage,
			defaultSearchQuery,
			defaultSortBy,
			defaultPageIndex,
		} = this.props;

		const deleteNotifications = {
			type: 'warning',
			title: 'Do you want to remove this Organization?',
			message: 'This action can not be undone',
			confirmBtnText: 'REMOVE',
			onConfirm: async () => {
				setAlert(null);
				try {
					await deleteOrganizationById(organizationId);
					this.fetchData({
						filterQuery: defaultSearchQuery,
						pageIndex: defaultPageIndex,
						pageSize: rowsPerPage,
						sortBy: defaultSortBy,
					});

					enqueueSnackbar(
						'Organization was successfully removed',
						{
							content: (key, message) => (
								<SnackMessage
									id={key}
									message={message}
									color="success"
								/>
							),
						}
					);
				} catch (e) {
					enqueueSnackbar(e.resolvedErrorMessage, {
						content: (key, message) => (
							<SnackMessage
								id={key}
								message={message}
								color="danger"
							/>
						),
					});
				}
			},
			onCancel: () => {
				setAlert(null);
			},
		};
		setAlert(deleteNotifications);
	};

	createOrganization = () => {
		this.props.history.push({ pathname: 'organizations/create' });
	}

	render() {
		const {
			classes,
			rowsPerPage,
			defaultSearchQuery,
			defaultSortBy,
			defaultPageIndex,
		} = this.props;

		return (
			<GridContainer>
				<GridItem xs={12} sm={12} md={12}>
					<Card>
						<CardHeader color="primary" icon>
							<CardIcon color="primary">
								<BusinessOutlinedIcon />
							</CardIcon>
						</CardHeader>
						<CardBody>
							<GridContainer>
								<GridItem xs={12} sm={12} md={12}>
									<ReactTable
										isPageRootComponent
										clickableRows={true}
										rowsWithSelection={false}
										columns={this.columns}
										data={this.state.data}
										fetchData={this.fetchData}
										rowsPerPage={rowsPerPage}
										defaultSearchQuery={defaultSearchQuery}
										defaultSortBy={defaultSortBy}
										defaultPageIndex={defaultPageIndex}
										loading={this.state.loading}
										pageCount={this.state.pageCount}
										totalRecordsCount={this.state.totalRecordsCount}
										detailsPath={
											`${layoutNames.portal}/organizations/details`
										}
										addRecordComponent={
											<div className={`${classes.right} ${classes.addRecord}`}>
												<Tooltip
													arrow
													title="Create Organization"
												>
													<span
														className={classes.imageLink}
														onClick={this.createOrganization}
													>
														<AddCircleOutlineOutlinedIcon />
													</span>
												</Tooltip>
											</div>
										}
									/>
								</GridItem>
							</GridContainer>
						</CardBody>
					</Card>
				</GridItem>
			</GridContainer>
		);
	}
}

const mapStateToProps = ({
	common: {
		tablesState: {
			organizations: {
				searchQuery,
				sortBy,
				page,
			},
		},
	},
	settings: { rowsPerPage },
}) => {
	return {
		rowsPerPage: rowsPerPage.organizations,
		defaultSearchQuery: searchQuery,
		defaultSortBy: sortBy,
		defaultPageIndex: page,
	};
};

const mapDispatchToProps = (dispatch) => ({
	setAlert: bindActionCreators(
		setAlertActionCreator,
		dispatch
	),
	setPageHeaderText: bindActionCreators(
		setPageHeaderTextActionCreator,
		dispatch
	),
	setRowsPerPage: bindActionCreators(
		setNumberRowsPerPageActionCreator,
		dispatch,
	),
	setTableState: bindActionCreators(
		setTableStateActionCreator,
		dispatch,
	),
});

export default compose(
	withSnackbar,
	connect(mapStateToProps, mapDispatchToProps),
	withStyles(customStyles),
)(Organizations);

